import { defineAsyncComponent } from 'vue';
import { publicPageLayoutKind } from './publicPageLayoutPolicy';

const neutralPublicLayout = defineAsyncComponent(() => import('@/Layouts/PublicLayout.vue'));

// Page modules are cached by both Vite and Node. Never retain the layout selected
// when a shared public page was first imported under a different tenant.
export function withPublicPageLayout(module, name, theme, tenantLayout) {
    const kind = publicPageLayoutKind(name, theme);
    if (!kind) return module;
    const layout = kind === 'tenant' ? tenantLayout() : neutralPublicLayout;
    return { ...module, default: { ...module.default, layout } };
}

// Inertia's preserveState keeps a VNode key, but Vue also requires the same
// component identity. Scope this cache to one browser app; SSR keeps the fresh
// per-request adapter above and never shares these component/layout objects.
export function createClientPublicPageLayoutResolver() {
    const pages = new WeakMap();
    const layouts = new Map();
    return (module, name, theme, tenantLayout) => {
        if (!publicPageLayoutKind(name, theme)) return module;
        let variants = pages.get(module.default);
        if (!variants) pages.set(module.default, variants = new Map());
        const key = `${theme}:${name}`;
        if (!variants.has(key)) {
            const resolveLayout = () => {
                if (!layouts.has(theme)) layouts.set(theme, tenantLayout());
                return layouts.get(theme);
            };
            variants.set(key, withPublicPageLayout(module, name, theme, resolveLayout));
        }
        return variants.get(key);
    };
}
